import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatCardModule } from "@angular/material/card"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatSelectModule } from "@angular/material/select"
import { MatDatepicker, MatDatepickerModule } from "@angular/material/datepicker"
import { MatDialog, MatDialogModule } from "@angular/material/dialog"
import { provideNativeDateAdapter } from "@angular/material/core"

import { LockoutService } from "../../services/lockout.service"
import { PermissionService } from "../../services/permission.service"
import { StationContextService } from "../../services/station-context.service"
import { StationStoreService } from "../../services/station-store.service"
import { canManageStationConfig, normalizeStationRole } from "../../utils/station-role.util"
import { Subject, takeUntil } from "rxjs"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { Station } from "../../models/station.model"
import { formatCalendarDate, parseIsoCalendarDate } from "../../utils/business-date"
import {
  LockedDatesListDialogComponent,
  LockedDatesListDialogData,
} from "./locked-dates-list-dialog.component"

/** Inline preview count (responsive CSS may show fewer on narrow screens). */
const LOCKED_DATES_PREVIEW = 8

@Component({
  selector: "app-schedule-day-lockout",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatDatepickerModule,
    MatDialogModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./schedule-day-lockout.component.html",
  styleUrl: "./schedule-day-lockout.component.css",
})
export class ScheduleDayLockoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  @ViewChild("lockPicker") lockPicker!: MatDatepicker<Date>

  form: FormGroup
  stations: Station[] = []
  loadingStations = false
  locking = false
  unlockingDate: string | null = null
  lockedDates: string[] = []
  loadingLockedDates = false
  minLockoutDate: Date
  businessTodayIso: string | null = null

  constructor(
    private fb: FormBuilder,
    private lockoutService: LockoutService,
    private permissionService: PermissionService,
    private stationContext: StationContextService,
    private stationStore: StationStoreService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private dialog: MatDialog,
  ) {
    this.minLockoutDate = new Date()

    this.form = this.fb.group({
      stationId: [null as number | null],
      lockoutDate: [null as Date | null, Validators.required],
    })
  }

  ngOnInit(): void {
    if (!this.canAccessLockoutPage) return
    this.permissionService.currentRole$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshLockoutPermissions()
    })
    this.loadStations()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get canAccessLockoutPage(): boolean {
    if (this.permissionService.getBaseRole() === "admin") return true
    return this.stationContext
      .getAssignments()
      .some((a) => canManageStationConfig(a.station_role))
  }

  get canManageLockout(): boolean {
    const sid = this.effectiveStationId
    const station = this.stations.find((s) => s.id === sid)
    const role = normalizeStationRole(
      station?.user_role ??
        (sid != null ? this.stationContext.getRoleForStation(sid) : null) ??
        this.permissionService.getCurrentRole(),
    )
    return canManageStationConfig(role)
  }

  /** Stations this user may lock/unlock (admin: all; otherwise owner/station_owner assignments). */
  get manageableStations(): Station[] {
    if (this.permissionService.getBaseRole() === "admin") return this.stations
    return this.stations.filter((s) =>
      canManageStationConfig(s.user_role ?? this.stationContext.getRoleForStation(s.id)),
    )
  }

  /**
   * Any user who manages more than one station needs the selector — previously only
   * admins and "owner" assignees got it, so a station_owner of two stations was stuck
   * on stations[0].
   */
  get showStationDropdown(): boolean {
    return this.manageableStations.length > 1
  }

  private refreshLockoutPermissions(): void {
    // Triggers template refresh for getters bound to canManageLockout / showStationDropdown.
  }

  get effectiveStationId(): number | null {
    return this.form.get("stationId")?.value ?? this.manageableStations[0]?.id ?? null
  }

  /** Current and future locked dates only (no past). */
  get upcomingLockedDates(): string[] {
    const today = this.businessTodayIso
    if (!today) return []
    return [...this.lockedDates]
      .filter((d) => d >= today)
      .sort()
  }

  get previewLockedDates(): string[] {
    return this.upcomingLockedDates.slice(0, LOCKED_DATES_PREVIEW)
  }

  get hasMoreLockedDates(): boolean {
    return this.upcomingLockedDates.length > LOCKED_DATES_PREVIEW
  }

  lockoutDateFilter = (d: Date | null): boolean => {
    if (!d) return false
    const iso = this.formatDate(d)
    if (this.lockedDates.includes(iso)) return false
    const minIso = this.businessTodayIso
    if (!minIso) return false
    return iso >= minIso
  }

  openDatePicker(): void {
    if (this.loadingLockedDates || this.effectiveStationId == null || this.locking) return
    this.lockPicker?.open()
  }

  onStationChange(): void {
    this.form.patchValue({ lockoutDate: null })
    // Never show (or filter the picker with) the previous station's locked dates.
    this.lockedDates = []
    this.refreshLockedDates()
  }

  openFullLockedList(): void {
    const sid = this.effectiveStationId
    if (sid == null) return
    const ref = this.dialog.open(LockedDatesListDialogComponent, {
      width: "min(480px, 92vw)",
      maxWidth: "92vw",
      maxHeight: "90vh",
      panelClass: "lockout-dates-dialog",
      autoFocus: "first-tabbable",
      data: {
        stationId: sid,
        dates: this.upcomingLockedDates,
      } as LockedDatesListDialogData,
    })
    ref.afterClosed().subscribe(() => this.refreshLockedDates())
  }

  private loadStations(): void {
    this.loadingStations = true
    this.stationStore.watchStations().pipe(takeUntil(this.destroy$)).subscribe({
      next: (stations) => {
        this.stations = stations
        this.loadingStations = false
        const manageable = this.manageableStations
        if (manageable.length > 0) {
          // watchStations() re-emits on refresh: keep the user's station unless it is gone,
          // otherwise a background refresh would silently retarget Lock/Unlock.
          const current = this.form.get("stationId")?.value
          if (!manageable.some((s) => s.id === current)) {
            this.form.patchValue({ stationId: manageable[0].id, lockoutDate: null })
            this.lockedDates = []
            this.refreshLockedDates()
          } else if (this.businessTodayIso == null && !this.loadingLockedDates) {
            this.refreshLockedDates()
          }
        }
      },
      error: () => {
        this.loadingStations = false
        this.snackbar.error("Failed to load stations", 4000)
      },
    })
  }

  refreshLockedDates(): void {
    const sid = this.effectiveStationId
    if (sid == null) return
    this.loadingLockedDates = true
    this.lockoutService.getLockedDates(sid).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        // Ignore a slow response for a station the user has already switched away from.
        if (this.effectiveStationId !== sid) return
        this.lockedDates = res.lockedDates || []
        if (res.businessToday) {
          this.businessTodayIso = res.businessToday
          this.minLockoutDate = parseIsoCalendarDate(res.businessToday)
        }
        this.loadingLockedDates = false
      },
      error: () => {
        if (this.effectiveStationId !== sid) return
        this.lockedDates = []
        this.loadingLockedDates = false
        this.snackbar.error("Could not load locked dates for this station", 4000)
      },
    })
  }

  lockDay(): void {
    if (this.form.invalid || this.locking) return
    const sid = this.effectiveStationId
    const d = this.form.get("lockoutDate")?.value as Date
    if (sid == null || !d) return

    if (this.isDateAlreadyLocked(d)) {
      this.snackbar.info("This date is already locked.", 4000)
      return
    }

    const iso = this.formatDate(d)
    this.confirmDialog
      .open({
        title: "Lock schedule date",
        message:
          "Are you sure you want to lock this schedule date?",
        confirmLabel: "Lock",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.locking = true
        this.lockoutService.lockScheduleDay(sid, { date: iso }).subscribe({
          next: (res) => {
            this.locking = false
            if (res.alreadyLocked) {
              this.snackbar.success(
                `Already locked. ${res.promosFilled} additional free slot(s) filled.`,
                5000,
              )
            } else {
              this.snackbar.success(
                `Locked. ${res.promosFilled} preemptible promo(s) placed in free slots.`,
                5000,
              )
            }
            this.refreshLockedDates()
            this.form.patchValue({ lockoutDate: null })
          },
          error: (err) => {
            this.locking = false
            const detail = err?.error?.error || "Failed to lock schedule date"
            this.snackbar.error(detail, 6000)
            this.refreshLockedDates()
          },
        })
      })
  }

  unlockDate(iso: string, event?: Event): void {
    event?.stopPropagation()
    const sid = this.effectiveStationId
    if (sid == null || this.unlockingDate) return

    this.confirmDialog
      .open({
        title: "Unlock schedule date",
        message: "Are you sure you want to unlock this schedule date?",
        confirmLabel: "Unlock",
        cancelLabel: "Cancel",
      })
      .subscribe((ok) => {
        if (!ok) return
        this.unlockingDate = iso
        this.lockoutService.unlockScheduleDay(sid, { date: iso }).subscribe({
          next: (res) => {
            this.unlockingDate = null
            if (res.unlocked) {
              if (this.effectiveStationId === sid) {
                this.lockedDates = res.lockedDates || []
              }
              this.snackbar.success(
                `Unlocked ${iso}. Removed ${res.promosRemoved} auto-filled promo(s).`,
                5000,
              )
            } else {
              this.snackbar.info("Date was not locked.", 4000)
            }
          },
          error: (err) => {
            this.unlockingDate = null
            this.snackbar.error(err?.error?.error || "Failed to unlock", 5000)
          },
        })
      })
  }

  isDateAlreadyLocked(d: Date | null): boolean {
    if (!d) return false
    return this.lockedDates.includes(this.formatDate(d))
  }

  formatDate(d: Date): string {
    return formatCalendarDate(d)
  }
}
